import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

/** The two panels of "Gabarits de documents", in their displayed order. */
export type DocumentTemplatesTab = 'assignments' | 'templates';

const TAB_QUERY_PARAM = 'tab';

const TAB_ORDER: readonly DocumentTemplatesTab[] = ['assignments', 'templates'];

type UseDocumentTemplatesTabResult = {
	readonly activeTab: DocumentTemplatesTab;
	readonly activeIndex: number;
	readonly setActiveIndex: (index: number) => void;
};

/**
 * Reads and writes the `tab` query parameter shared by the page header and the tab view: an absent
 * or unrecognized value resolves to `assignments`, so a bare reload or a shared link always lands on
 * the same tab. Both consumers call this hook rather than lifting the state themselves, so the URL
 * stays the single source of truth and survives a remount of either.
 */
function useDocumentTemplatesTab(): UseDocumentTemplatesTabResult {
	const [searchParams, setSearchParams] = useSearchParams();

	const activeTab: DocumentTemplatesTab =
		searchParams.get(TAB_QUERY_PARAM) === 'templates' ? 'templates' : 'assignments';

	const activeIndex = TAB_ORDER.indexOf(activeTab);

	const setActiveIndex = React.useCallback(
		(index: number) => {
			const nextTab = TAB_ORDER[index] ?? 'assignments';

			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);
					next.set(TAB_QUERY_PARAM, nextTab);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	return { activeTab, activeIndex, setActiveIndex };
}

export default useDocumentTemplatesTab;
